import type {
  UseRadioGroupProps,
  UseRadioGroupReturn
} from '@chakra-ui/react'
import {
  FormErrorMessage,
  useRadioGroup
} from '@chakra-ui/react'
import { createContext } from '@chakra-ui/react-utils'
import type { HTMLChakraProps, ThemingProps } from '@chakra-ui/system'
import { chakra, forwardRef } from '@chakra-ui/system'
import { __DEV__, cx } from '@chakra-ui/utils'
import type { Control } from 'BrokerTsLib/src/rhf'
import { useController, useWatch } from 'BrokerTsLib/src/rhf'
import { useMemo } from 'react'

export interface RadioIconGroupContext
  extends Pick<UseRadioGroupReturn, 'onChange' | 'value' | 'name' | 'isDisabled' | 'isFocusable'>,
  Omit<ThemingProps<'Radio'>, 'orientation'> { }

const [RadioIconGroupProvider, useRadioIconGroupContext] = createContext<RadioIconGroupContext>({
  name: 'RadioIconGroupContext',
  strict: false,
})

export { useRadioIconGroupContext }

type Omitted = 'onChange' | 'value' | 'defaultValue' | 'defaultChecked' | 'children' | 'name'
export interface RadioIconGroupProps
  extends UseRadioGroupProps
  , Omit<HTMLChakraProps<'div'>, Omitted>
  , Omit<ThemingProps<'Radio'>, 'orientation'> {
  children: React.ReactNode
  control: Control<any>
  name: string
}

/**
 * Used for multiple radios which are bound in one group,
 * and it indicates which option is selected.
 *
 * @see Docs https://chakra-ui.com/radio
 */
export const RadioIconGroup = forwardRef<RadioIconGroupProps, 'div'>((props, ref) => {
  const {
    colorScheme,
    size,
    variant,
    children,
    name,
    className,
    control,
    isDisabled,
    isFocusable,
    onChange,
    ...rest
  } = props

  const { field, formState: { errors } } = useController({ control, name })
  const value = useWatch({ control, name }) // pri setovani hodnoty z vinonline se neprerendrovala tato komponenta., 

  const { getRootProps, htmlProps } = useRadioGroup({
    onChange: onChange ?? field.onChange,
    value: value,
    name,
    ...rest,
  })

  const group = useMemo(
    () => ({
      name,
      size,
      onChange: field.onChange,
      colorScheme,
      value,
      variant,
      isDisabled,
      isFocusable,
    }),
    [name, size, field.onChange, colorScheme, value, variant, isDisabled, isFocusable],
  )

  const groupProps = getRootProps(htmlProps, ref)
  const _className = cx('chakra-radio-group', className)
  const errorMessage = errors && errors[name] && errors[name]?.message

  return (
    <RadioIconGroupProvider value={group}>
      <chakra.div {...groupProps} className={_className}>
        {children}
      </chakra.div>
      {errorMessage && <FormErrorMessage>{errorMessage as string}</FormErrorMessage>}
    </RadioIconGroupProvider>
  )
})

if (__DEV__) {
  RadioIconGroup.displayName = 'RadioIconGroup'
}
